class QuickAdd extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.content = this.querySelector('.custom-quick-add__content');

    this.registerEventListeners();
  }

  registerEventListeners() {
    // Use event delegation to handle dynamically added buttons
    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-quick-add]')) {
        this.handleOpen(event);
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      if (!event.target.closest('[data-quick-add]')) return;
      this.handleOpen(event);
    });

    this.closeButton = this.querySelector('[data-close]');
    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.closeDrawer.bind(this));
    }

    this.overlay = this.querySelector('.custom-quick-add__overlay');
    if (this.overlay) {
      this.overlay.addEventListener('click', this.closeDrawer.bind(this));
    }

    // Listen for cart drawer overlay clicks and close the popup if the drawer is closed
    document.addEventListener('click', (event) => {
      if (event.target.closest('#CartDrawer-Overlay')) {
        this.closeDrawer();
      }
    });
  }

  handleOpen(event) {
    event.preventDefault();

    const button = event.target.closest('[data-quick-add]');
    if (!button) return;

    // Get the add button and add loading state
    const loadingSpinner = button.querySelector('.loading__spinner');

    // Add loading state like Dawn's add to cart button
    button.setAttribute('aria-disabled', true);
    button.classList.add('loading');
    if (loadingSpinner) {
      loadingSpinner.classList.remove('hidden');
    }

    const productHandle = button.dataset.productHandle;

    fetch(`/products/${productHandle}.js`)
      .then((res) => res.json())
      .then((product) => {
        this.product = product;
        this.content.innerHTML = this.renderProductForm(product);
        const variantSelects = this.content.querySelector('variant-selects');
        if (variantSelects) {
          variantSelects.addEventListener(
            'change',
            this.handleOptionsChange.bind(this),
          );
        }
        const form = this.content.querySelector('form');
        if (form) {
          form.addEventListener('submit', this.handleSubmit.bind(this));
        }
        this.openDrawer();
      });

    // Remove loading state like Dawn's add to cart button
    button.classList.remove('loading');
    button.removeAttribute('aria-disabled');
    if (loadingSpinner) {
      loadingSpinner.classList.add('hidden');
    }
  }

  handleOptionsChange() {
    if (!this.product) return;

    const selectedOptions = this.product.options.map((option) => {
      const fieldset = this.content.querySelector(
        `fieldset#${CSS.escape(option.name)}`,
      );
      const checkedInput = fieldset?.querySelector('input:checked');
      return checkedInput ? checkedInput.value : null;
    });

    const selectedVariant = this.product.variants.find((variant) =>
      selectedOptions.every((value, index) => value === variant.options[index]),
    );

    const variantInput = this.content.querySelector('input[name="id"]');
    if (variantInput && selectedVariant) {
      variantInput.value = selectedVariant.id;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  handleSubmit(event) {
    event.preventDefault();
    const formData = new FormData(event.target);
    const variantId = formData.get('id');
    const quantity = formData.get('quantity');
    this.addToCart(variantId, quantity);
  }

  addToCart(variantId, quantity) {
    const items = [
      {
        id: parseInt(variantId, 10),
        quantity: parseInt(quantity, 10) || 1,
      },
    ];

    const cartDrawer = document.querySelector('cart-drawer');
    const cartNotification = document.querySelector('cart-notification');
    const cartIcon = document.querySelector('cart-icon-bubble');

    // Get the submit button and add loading state
    const submitButton = this.content.querySelector('button[type="submit"]');
    const loadingSpinner = submitButton.querySelector('.loading__spinner');

    // Add loading state like Dawn's add to cart button
    submitButton.setAttribute('aria-disabled', true);
    submitButton.classList.add('loading');
    if (loadingSpinner) {
      loadingSpinner.classList.remove('hidden');
    }

    const body = {
      items,
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    };
    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })
      .then((response) =>
        response.json().then((data) => ({ ok: response.ok, data })),
      )
      .then(({ ok, data: response }) => {
        // Use Dawn's built-in cart refresh functionality
        if (cartDrawer) {
          cartDrawer.renderContents(response);
        }
        if (cartNotification) {
          cartNotification.renderContents(response);
        }

        // Close the modal after successful update
        this.closeDrawer();

        // Publish cart update event for other components
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'quick-add',
          cartData: response,
        });
      })
      .catch((error) => {
        console.error('Error:', error);
      })
      .finally(() => {
        // Remove loading state like Dawn's add to cart button
        submitButton.classList.remove('loading');
        submitButton.removeAttribute('aria-disabled');
        if (loadingSpinner) {
          loadingSpinner.classList.add('hidden');
        }
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
      {
        id: 'cart-notification-product',
        selector: '#cart-notification-product',
      },
    ];
  }

  isOptionValueAvailable(product, optionIndex, value) {
    return product.variants.some(
      (variant) =>
        variant.available && variant.options?.[optionIndex] === value,
    );
  }

  renderProductForm(product) {
    const firstAvailableVariant = product.variants.find(
      (variant) => variant.available,
    );

    return `
        <div class="custom-quick-add__product-info">
          <img src="${product.featured_image}" alt="${product.title}" class="custom-quick-add__product-image" />
          <h3>${product.title}</h3>
        </div>
        <hr />
        <variant-selects>
          ${product.options
            .map((option, optionIndex) => {
              const firstAvailableValue = option.values.find((value) =>
                this.isOptionValueAvailable(product, optionIndex, value),
              );

              return `
                <div class="product-form__input__wrapper">
                  <fieldset class="js product-form__input product-form__input--pill ${option.name.toLowerCase().replace(/\s+/g, '-')}" id="${option.name}">
                    ${option.values
                      .map((value, valueIndex) => {
                        const optionDisabled = !this.isOptionValueAvailable(
                          product,
                          optionIndex,
                          value,
                        );

                        return `
                        <div class="product-form__input--pill-wrapper" id="wrapper-${value}">
                          <input type="radio" id="${option.name}--${value}" ${optionDisabled ? 'class="disabled" disabled' : ''} name="${option.name}" value="${value}" ${value === firstAvailableValue ? 'checked' : ''} />
                          <label for="${option.name}--${value}" class="${value.toLowerCase().replace(/\s+/g, '-')}${optionDisabled ? ' disabled' : ''}">
                            ${
                              option.name === 'Waistband' &&
                              value === 'CLASSIC BELT'
                                ? `
                              <svg viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M2.70903 3.58698C3.16296 2.22518 2.82112 1.26127 2.51181 0.826035C2.46947 0.766452 2.51034 0.67395 2.58344 0.67395H9.37371C9.43471 0.67395 9.47451 0.738336 9.44932 0.793883C9.00054 1.78342 9.07719 2.82392 9.1792 3.2486C9.1816 3.25861 9.18598 3.26776 9.19195 3.27614C11.7453 6.86279 10.9244 10.7419 10.1756 12.2818C10.162 12.3097 10.1342 12.3261 10.1031 12.3261H2.10441C2.07764 12.3261 2.05213 12.3125 2.03693 12.2904C-0.187391 9.06325 2.06407 5.52187 2.70903 3.58698Z" stroke="currentColor" stroke-width="0.820475"></path>
  <path d="M10.3796 6.86176C7.08182 8.33909 3.13022 7.49297 1.47697 6.85379C1.43406 6.8372 1.38711 6.85928 1.37179 6.90266C0.656311 8.92835 1.49105 11.2995 2.02513 12.2847C2.03934 12.3109 2.06634 12.3261 2.09614 12.3261H5.9319H9.77046C9.79875 12.3261 9.82521 12.3114 9.84001 12.2872C11.0701 10.2834 10.8021 7.92872 10.491 6.91077C10.4767 6.864 10.4243 6.84177 10.3796 6.86176Z" fill="currentColor" fill-opacity="0.3"></path>
  <path d="M5.93207 11.0314L5.9319 12.3261M5.9319 12.3261H9.77046C9.79875 12.3261 9.82521 12.3114 9.84001 12.2872C11.0701 10.2834 10.8021 7.92872 10.491 6.91077C10.4767 6.864 10.4243 6.84177 10.3796 6.86176C7.08182 8.33909 3.13022 7.49297 1.47697 6.85379C1.43406 6.8372 1.38711 6.85928 1.37179 6.90266C0.656311 8.92835 1.49105 11.2995 2.02513 12.2847C2.03934 12.3109 2.06634 12.3261 2.09614 12.3261H5.9319Z" stroke="currentColor" stroke-width="0.820475"></path>
  </svg>
                            `
                                : ''
                            }
                            ${
                              option.name === 'Waistband' &&
                              value === 'HIGH BELT'
                                ? `
                              <svg viewBox="0 0 10 13" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M1.99171 3.83725C2.40665 2.59243 2.09417 1.71131 1.81143 1.31346C1.77273 1.259 1.81009 1.17444 1.87691 1.17444H8.08392C8.13968 1.17444 8.17607 1.23329 8.15304 1.28407C7.74281 2.18861 7.81287 3.13973 7.90612 3.52794C7.90831 3.53709 7.91232 3.54545 7.91778 3.55311C10.2518 6.83168 9.50144 10.3776 8.81696 11.7852C8.80454 11.8108 8.77907 11.8257 8.75066 11.8257H1.43902C1.41455 11.8257 1.39123 11.8132 1.37734 11.7931C-0.655921 8.84314 1.40215 5.60594 1.99171 3.83725Z" stroke="currentColor" stroke-width="0.75"></path>
  <path d="M7.83912 3.56815C4.851 4.92731 3.77692 3.86554 2.33962 3.27165C2.30357 3.25675 2.26141 3.27394 2.24405 3.30887C-0.630946 9.09364 0.853216 10.8912 1.3663 11.7882C1.37983 11.8118 1.40441 11.8258 1.43165 11.8258H4.93793H8.44678C8.47264 11.8258 8.49651 11.8128 8.51003 11.7908C9.66853 9.90129 9.95238 6.52197 7.93334 3.59331C7.91274 3.56343 7.87215 3.55312 7.83912 3.56815Z" fill="currentColor" fill-opacity="0.3"></path>
  <path d="M4.93809 10.6423L4.93793 11.8258M4.93793 11.8258H8.44678C8.47264 11.8258 8.49651 11.8128 8.51003 11.7908C9.66853 9.90129 9.95238 6.52197 7.93334 3.59331C7.91274 3.56343 7.87215 3.55312 7.83912 3.56815C4.851 4.92731 3.77692 3.86554 2.33962 3.27165C2.30357 3.25675 2.26141 3.27394 2.24405 3.30887C-0.630946 9.09364 0.853216 10.8912 1.3663 11.7882C1.37983 11.8118 1.40441 11.8258 1.43165 11.8258H4.93793Z" stroke="currentColor" stroke-width="0.75"></path>
  </svg>
                            `
                                : ''
                            }
                            <span>${value}</span>
                            <span class="visually-hidden label-unavailable">Variant sold out of unavailable</span>
                          </label>
                        </div>
                      `;
                      })
                      .join('')}
                  </fieldset>
                </div>
              `;
            })
            .join('')}
        </variant-selects>
        <form>
          <input type="hidden" name="id" value="${firstAvailableVariant?.id || ''}" />
          <quantity-input class="quantity cart-quantity">
            <button class="quantity__button" name="minus" type="button">
              <span class="visually-hidden">
                Decreaase quantity for ${product.title}
              </span>
              <span class="svg-wrapper">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-minus" viewBox="0 0 10 2"><path fill="currentColor" fill-rule="evenodd" d="M.5 1C.5.7.7.5 1 .5h8a.5.5 0 1 1 0 1H1A.5.5 0 0 1 .5 1" clip-rule="evenodd"></path></svg>
              </span>
            </button>
            <input
              name="quantity"
              class="quantity__input"
              type="number"
              value="1"
              min="1"
            >
            <button class="quantity__button" name="plus" type="button">
              <span class="visually-hidden">
                Increase quantity for ${product.title}
              </span>
              <span class="svg-wrapper">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-plus" viewBox="0 0 10 10"><path fill="currentColor" fill-rule="evenodd" d="M1 4.51a.5.5 0 0 0 0 1h3.5l.01 3.5a.5.5 0 0 0 1-.01V5.5l3.5-.01a.5.5 0 0 0-.01-1H5.5L5.49.99a.5.5 0 0 0-1 .01v3.5l-3.5.01z" clip-rule="evenodd"></path></svg>
              </span>
            </button>
          </quantity-input>
          <button class="button button--primary" type="submit">
            Add Item
            <div class="loading__spinner hidden">
              <svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"></circle></svg>
            </div>
          </button>
        </form>
      `;
  }

  openDrawer() {
    this.setAttribute('open', '');
  }

  closeDrawer() {
    this.removeAttribute('open');
  }
}

customElements.define('quick-add', QuickAdd);
